import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPlans from './pages/admin/AdminPlans';
import AdminFeedback from './pages/admin/AdminFeedback';
import AdminArchitecture from './pages/admin/AdminArchitecture';
import Login from './pages/Login';
import Register from './pages/Register';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Scopes from './pages/Scopes';
import Documents from './pages/Documents';
import Proposals from './pages/Proposals';
import ProposalReview from './pages/ProposalReview';
import Contracts from './pages/Contracts';
import Invoices from './pages/Invoices';
import Inbox from './pages/Inbox';
import Notifications from './pages/Notifications';
import Payments from './pages/Payments';
import SubmitInvoice from './pages/SubmitInvoice';
import Vendors from './pages/Vendors';
import Customers from './pages/Customers';
import NewOffPlatformContract from './pages/NewOffPlatformContract';
import PurchaseOrders from './pages/PurchaseOrders';
import Budget from './pages/Budget';
import SignContract from './pages/SignContract';
import SignChangeOrder from './pages/SignChangeOrder';
import ProjectDetail from './pages/ProjectDetail';
import Settings from './pages/Settings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VendorOnboarding from './pages/VendorOnboarding';
import Admin from './pages/Admin';
import Schedule from './pages/Schedule';
import AccountsReceivable from './pages/AccountsReceivable';
import CreditMemos from './pages/CreditMemos';
import SubcontractorInvoice from './pages/SubcontractorInvoice';
import GCSettings from './pages/GCSettings';
import CompanyProfile from './pages/CompanyProfile';
import { CircularProgress, Box } from '@mui/material';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  return user?.role === 'admin' ? children : <Navigate to="/dashboard" />;
};

const SubRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  return user?.role === 'subcontractor' ? children : <Navigate to="/dashboard" />;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
        <Route path="/admin/feedback" element={<AdminFeedback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
          <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
        <Route path="/projects/:id" element={<PrivateRoute><ProjectDetail /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
          <Route path="/scopes" element={<PrivateRoute><Scopes /></PrivateRoute>} />
          <Route path="/documents" element={<PrivateRoute><Documents /></PrivateRoute>} />
          <Route path="/customers" element={<PrivateRoute><Customers /></PrivateRoute>} />
          <Route path="/customers/:customerId/new-contract" element={<PrivateRoute><NewOffPlatformContract /></PrivateRoute>} />
          <Route path="/proposals" element={<PrivateRoute><Proposals /></PrivateRoute>} />
          <Route path="/proposal-review" element={<PrivateRoute><ProposalReview /></PrivateRoute>} />
          <Route path="/contracts" element={<PrivateRoute><Contracts /></PrivateRoute>} />
          <Route path="/invoices" element={<PrivateRoute><Invoices /></PrivateRoute>} />
          <Route path="/inbox" element={<PrivateRoute><Inbox /></PrivateRoute>} />
          <Route path="/payments" element={<PrivateRoute><Payments /></PrivateRoute>} />
          <Route path="/submit-invoice" element={<PrivateRoute><SubcontractorInvoice /></PrivateRoute>} />
          <Route path="/vendors" element={<PrivateRoute><Vendors /></PrivateRoute>} />
          <Route path="/vendor-onboarding" element={<VendorOnboarding />} />
          <Route path="/sign-contract" element={<SignContract />} />
          <Route path="/sign-change-order" element={<SignChangeOrder />} />
          <Route path="/purchase-orders" element={<PrivateRoute><PurchaseOrders /></PrivateRoute>} />
          <Route path="/budget" element={<PrivateRoute><Budget /></PrivateRoute>} />
          <Route path="/credit-memos" element={<PrivateRoute><CreditMemos /></PrivateRoute>} />
          <Route path="/schedule" element={<PrivateRoute><Schedule /></PrivateRoute>} />
          <Route path="/ar" element={<SubRoute><AccountsReceivable /></SubRoute>} />
          <Route path="/company-profile" element={<PrivateRoute><CompanyProfile /></PrivateRoute>} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="/admin/architecture" element={<AdminRoute><AdminArchitecture /></AdminRoute>} />
          <Route path="/admin/gc-settings" element={<AdminRoute><GCSettings /></AdminRoute>} />
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
